import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, addDoc, getDoc, getDocs, doc,
  query, where, serverTimestamp, setDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, Button,
  Chip, Grid, Avatar, Collapse, Divider, Tooltip, LinearProgress,
  TextField, Switch, FormControlLabel, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// ── Statuskonfig ───────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    draft:     { label: 'Utkast',    color: 'default' },
    submitted: { label: 'Inlämnad', sx: { bgcolor: '#FF6B00', color: '#fff' } },
    reviewed:  { label: 'Genomförd', color: 'success' },
  };
  const cfg = map[status] || map.draft;
  return <Chip size="small" label={cfg.label} color={cfg.color} sx={cfg.sx} />;
}

// ── Ifyllnadsgrad ──────────────────────────────────────────────────
function calcProgress(iup) {
  if (!iup) return 0;
  if (iup.type === 'medarbetare') {
    const criteria = ['kompetens', 'kreativitet', 'socialKompetens', 'hangivenhet', 'enkelt', 'affarsmassighet'];
    const filled = criteria.filter(k => iup[k]?.rating).length;
    const goal   = iup.individualGoal?.trim() ? 1 : 0;
    const mutual = iup.mutualCommitments?.trim() ? 1 : 0;
    return Math.round(((filled + goal + mutual) / 8) * 100);
  } else {
    const qs = ['q1','q2','q3','q4','q5','q6','q9'].filter(k => iup[k]?.trim()).length;
    const q7 = (iup.q7 || []).some(v => v?.trim()) ? 1 : 0;
    const q8 = (iup.q8 || []).some(v => v?.trim()) ? 1 : 0;
    return Math.round(((qs + q7 + q8) / 9) * 100);
  }
}

function ProgressBar({ iup }) {
  if (!iup) return null;
  const pct = calcProgress(iup);
  const color = pct >= 80 ? '#2e7d32' : pct >= 40 ? '#e65100' : '#9e9e9e';
  return (
    <Tooltip title={`${pct}% ifyllt`} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default' }}>
        <Box sx={{ width: 44, height: 5, bgcolor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, transition: 'width .3s' }} />
        </Box>
        <Typography variant="caption" sx={{ color, fontSize: '0.65rem', fontWeight: 'bold', minWidth: 26 }}>
          {pct}%
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ── IUP-hämtning ──────────────────────────────────────────────────
// Frågan måste kunna bevisas tillåten av firestore.rules (iups):
// VD/superadmin och gruppledare frågar på userId; AO-ansvariga får bara läsa
// IUP:er i sitt avdelningsträd och måste därför även filtrera på
// medarbetarens avdelning. IUP:er som ligger på en tidigare avdelning syns
// då inte för AO-ansvarig. En nekad fråga ger en tom lista i stället för att
// hela trädet fallerar.
async function fetchAllIupsForUser(user, viewer) {
  const viewerIsHR = viewer?.role === 'vd' || viewer?.role === 'superadmin';
  const viewerIsAO = !viewerIsHR && (viewer?.role === 'ao_ansvarig' || viewer?.isDepartmentManager);
  const filters = [where('userId', '==', user.id)];
  if (viewerIsAO) {
    if (!user.departmentId) return [];
    filters.push(where('departmentId', '==', user.departmentId));
  }
  let snap;
  try {
    snap = await getDocs(query(collection(db, 'iups'), ...filters));
  } catch (e) {
    console.warn(`IUP:er för ${user.id} kunde inte läsas:`, e.code || e.message);
    return [];
  }
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const at = a.createdAt?.toDate?.()?.getTime() ?? 0;
      const bt = b.createdAt?.toDate?.()?.getTime() ?? 0;
      return bt - at;
    });
}

// ── Sub-avdelnings-IDn via offerCode-prefix ────────────────────────
function getSubDeptIds(allDepts, baseDeptIds) {
  const baseCodes = baseDeptIds
    .map(id => allDepts.find(d => d.id === id)?.offerCode || '')
    .filter(Boolean);
  const fromPrefix = baseCodes.length > 0
    ? allDepts
        .filter(d => baseCodes.some(c => c && (d.offerCode === c || d.offerCode?.startsWith(c + '-'))))
        .map(d => d.id)
    : [];
  return [...new Set([...baseDeptIds, ...fromPrefix])];
}

// ── Rekursiv laddning av team-träd ─────────────────────────────────
async function loadTeamTree(userObj, allDepts, depth, currentUid, treeRole, viewer = userObj) {
  const { id: uid, departmentId, managedDepartmentIds } = userObj;
  const effectiveRole = treeRole || userObj.role;
  let usersSnap;

  if (effectiveRole === 'vd' || effectiveRole === 'superadmin') {
    usersSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'ao_ansvarig')));
  } else if (effectiveRole === 'ao_ansvarig') {
    const baseDeptIds = [departmentId, ...(managedDepartmentIds || [])].filter(Boolean);
    const deptIds = [...new Set(getSubDeptIds(allDepts, baseDeptIds))].filter(Boolean);
    if (!deptIds.length) return [];
    usersSnap = await getDocs(query(
      collection(db, 'users'),
      where('departmentId', 'in', deptIds.slice(0, 30))
    ));
  } else if (effectiveRole === 'gruppledare') {
    usersSnap = await getDocs(query(collection(db, 'users'), where('gruppledarId', '==', uid)));
  } else {
    return [];
  }

  const roleFilter = (effectiveRole === 'vd' || effectiveRole === 'superadmin')
    ? (u) => u.role === 'ao_ansvarig'
    : (effectiveRole === 'ao_ansvarig')
    ? (u) => u.role === 'gruppledare'
    : () => true;

  const users = usersSnap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(u => u.id !== currentUid && roleFilter(u))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'sv'));

  return await Promise.all(users.map(async user => {
    const iups = await fetchAllIupsForUser(user, viewer);
    const children = depth > 0
      ? await loadTeamTree(user, allDepts, depth - 1, currentUid, user.role, viewer)
      : [];
    return { user, iups, latestIup: iups[0] || null, children };
  }));
}

// ── Skapa nytt IUP (carry-forward) ────────────────────────────────
async function createIupForUser(targetUser, creatorUid, latestIup, overrideFormData) {
  const iupType = (targetUser.role === 'ao_ansvarig' || targetUser.isDepartmentManager)
    ? 'ao_ansvarig'
    : targetUser.role === 'gruppledare' ? 'gruppledare' : 'medarbetare';
  const today = new Date().toISOString().slice(0, 10);
  let formData = {};
  if (latestIup) {
    if (iupType === 'medarbetare') {
      formData = {
        kompetens: latestIup.kompetens || {}, kreativitet: latestIup.kreativitet || {},
        socialKompetens: latestIup.socialKompetens || {}, hangivenhet: latestIup.hangivenhet || {},
        enkelt: latestIup.enkelt || {}, affarsmassighet: latestIup.affarsmassighet || {},
        individualGoal: latestIup.individualGoal || '', otherComments: latestIup.otherComments || '',
        mutualCommitments: latestIup.mutualCommitments || '',
      };
    } else {
      formData = {
        q1: latestIup.q1 || '', q2: latestIup.q2 || '', q3: latestIup.q3 || '',
        q4: latestIup.q4 || '', q5: latestIup.q5 || '', q6: latestIup.q6 || '',
        q7: latestIup.q7 || ['','',''], q8: latestIup.q8 || ['','',''],
        q9: latestIup.q9 || '', goals: latestIup.goals || [], activities: latestIup.activities || {},
      };
    }
  }
  const finalFormData = overrideFormData !== undefined ? overrideFormData : formData;
  const ref = await addDoc(collection(db, 'iups'), {
    userId: targetUser.id, userName: targetUser.displayName || targetUser.email || '',
    departmentId: targetUser.departmentId || '', type: iupType, period: today,
    status: 'draft', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    submittedAt: null, comments: [], seenByManager: {}, ...finalFormData,
  });
  return ref.id;
}

// ── AI-sammanfattning av tidigare IUP ─────────────────────────────
async function summarizeIupAI(iup) {
  const isLedar = iup.type !== 'medarbetare';
  let content = '';
  if (!isLedar) {
    const CRIT_KEYS = ['kompetens','kreativitet','socialKompetens','hangivenhet','enkelt','affarsmassighet'];
    content = CRIT_KEYS.map(k => {
      const v = iup[k] || {};
      return `${k}: ${v.rating || ''} — ${v.motivation || ''}`;
    }).join('\n') +
    `\nMål: ${(iup.goals || []).map((g,i) => `${i+1}. ${g.mal}`).join(' | ') || iup.individualGoal || ''}` +
    `\nÖmsesidiga åtaganden: ${iup.mutualCommitments || ''}`;
  } else {
    content = ['q1','q2','q3','q4','q5','q6'].map(k => `${k}: ${iup[k] || ''}`).join('\n') +
      `\nq7: ${(iup.q7 || []).join(' | ')}` +
      `\nq8: ${(iup.q8 || []).join(' | ')}` +
      `\nq9: ${iup.q9 || ''}`;
  }

  const prompt = !isLedar
    ? `Du sammanfattar en medarbetares IUP-rapport kortfattat för att ge en bra startpunkt inför nästa samtal. Returnera ENBART giltig JSON med dessa fält (kortare text, max 2 meningar per fält):
{"kompetens":{"date":"","rating":"","motivation":""},"kreativitet":{"date":"","rating":"","motivation":""},"socialKompetens":{"date":"","rating":"","motivation":""},"hangivenhet":{"date":"","rating":"","motivation":""},"enkelt":{"date":"","rating":"","motivation":""},"affarsmassighet":{"date":"","rating":"","motivation":""},"goals":[{"mal":"","utfall":""}],"otherComments":"","mutualCommitments":""}

IUP-innehåll:\n${content}`
    : `Du sammanfattar en ledares IUP-rapport kortfattat för att ge en bra startpunkt inför nästa samtal. Returnera ENBART giltig JSON med dessa fält (kortare text, max 2 meningar per fält):
{"q1":"","q2":"","q3":"","q4":"","q5":"","q6":"","q7":["","",""],"q8":["","",""],"q9":""}

IUP-innehåll:\n${content}`;

  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 2000,
    messages: [{ role: 'user', content: prompt }]
  });
  const raw = json.content?.[0]?.text || '';
  try { return JSON.parse(raw); } catch {
    const m = raw.match(/\{[\s\S]*\}/);
    if (m) return JSON.parse(m[0]);
    throw new Error('Kunde inte tolka AI-svaret.');
  }
}

// ── Riktning: spara till iupDirections ─────────────────────────────
async function saveDirection(id, data) {
  await setDoc(doc(db, 'iupDirections', id), {
    ...data, updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Generell riktning (VD / AO, visas överst) ──────────────────────
function GeneralDirectionCard({ myRole, myId, departmentId, departmentName, vdDir, myDir, onSave }) {
  const [text, setText] = useState(myDir?.content || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setText(myDir?.content || ''); }, [myDir]);

  const isVD = myRole === 'vd' || (myRole === 'superadmin' && !departmentId);

  async function handleSave() {
    setSaving(true);
    try {
      await saveDirection(`general_${myId}`, {
        scope: 'general', authorId: myId, authorRole: myRole,
        departmentId: departmentId || null, content: text,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSave?.({ content: text });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Paper sx={{ p: 2.5, mb: 3, borderLeft: '4px solid #FF6B00' }}>
      <Typography variant="subtitle2" fontWeight="bold"
        sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
        Generell riktning {isVD ? 'för bolaget' : `för ${departmentName || 'mitt affärsområde'}`}
      </Typography>

      {!isVD && vdDir?.content && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '3px solid #bdbdbd' }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
            VDs riktning för bolaget
          </Typography>
          <Typography variant="body2">{vdDir.content}</Typography>
        </Box>
      )}

      <TextField
        multiline minRows={3} fullWidth size="small"
        placeholder={`Beskriv önskad generell riktning ${isVD ? 'för bolaget' : `för ${departmentName || 'ditt affärsområde'}`}...`}
        value={text}
        onChange={e => setText(e.target.value)}
        sx={{ mb: 1.5 }}
      />
      <Button size="small" variant="contained" disabled={saving} onClick={handleSave}
        sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
        {saving ? 'Sparar...' : saved ? '✓ Sparat' : 'Spara riktning'}
      </Button>
    </Paper>
  );
}

// ── Individuell riktning per person ────────────────────────────────
function DirectionPanel({ targetUserId, authorId, authorRole, direction, upstreamDirs, onSave }) {
  const [text, setText] = useState(direction?.content || '');
  const [shared, setShared] = useState(direction?.sharedWithTarget || false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setText(direction?.content || '');
    setShared(direction?.sharedWithTarget || false);
  }, [direction]);

  async function handleSave() {
    setSaving(true);
    try {
      await saveDirection(`individual_${authorId}_${targetUserId}`, {
        scope: 'individual', authorId, authorRole,
        targetUserId, content: text, sharedWithTarget: shared,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
      {upstreamDirs?.filter(d => d.content).map((d, i) => (
        <Box key={i} sx={{ mb: 1.5, p: 1.25, bgcolor: '#f0f0f0', borderRadius: 1,
          borderLeft: '3px solid #bdbdbd' }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 0.25, fontWeight: 'bold' }}>
            {d.label}
          </Typography>
          <Typography variant="caption">{d.content}</Typography>
        </Box>
      ))}

      <TextField
        multiline minRows={2} fullWidth size="small"
        placeholder="Beskriv önskad förflyttning för denna person..."
        value={text}
        onChange={e => setText(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Switch size="small" checked={shared} onChange={e => setShared(e.target.checked)} />}
          label={<Typography variant="caption">Dela med personen</Typography>}
          sx={{ m: 0 }}
        />
        <Button size="small" variant="contained" disabled={saving} onClick={handleSave}
          sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, fontSize: '0.7rem' }}>
          {saving ? '...' : saved ? '✓ Sparat' : 'Spara'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Kompakt rad för djupare nivåer ─────────────────────────────────
function MemberRow({ member, depth, navigate, onCreateNew, creatingFor, expandedChildren,
  setExpandedChildren, expandedHist, setExpandedHist }) {
  const { user, iups, latestIup, children } = member;
  const IUP_TYPE_LABELS = { gruppledare: 'GL-IUP', ao_ansvarig: 'AO-IUP', medarbetare: 'MA-IUP' };
  const childOpen  = !!expandedChildren[user.id];
  const histOpen   = !!expandedHist[user.id];
  const initials   = (user.displayName || user.email || '?')[0].toUpperCase();
  const bgDepth    = depth === 1 ? '#fafafa' : '#f5f5f5';
  const avatarSize = depth === 1 ? 30 : 24;
  const avatarFs   = depth === 1 ? 13 : 11;
  const textVariant = depth === 1 ? 'body2' : 'caption';
  const hasChildren = children && children.length > 0;

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1, bgcolor: bgDepth,
        pl: depth === 1 ? 3 : 5,
      }}>
        <Avatar sx={{ width: avatarSize, height: avatarSize, bgcolor: '#bdbdbd', fontSize: avatarFs, flexShrink: 0 }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={textVariant} fontWeight="medium" noWrap>
            {user.displayName || user.email}
          </Typography>
        </Box>
        {latestIup ? (
          <>
            <ProgressBar iup={latestIup} />
            <StatusChip status={latestIup.status} />
            <Button size="small" variant="text"
              onClick={() => navigate('/hr/iup/' + latestIup.id)}
              sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75, flexShrink: 0 }}>
              Öppna
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              Inget IUP
            </Typography>
            <Button size="small" variant="text" disabled={creatingFor === user.id}
              onClick={() => onCreateNew({ user, iups, latestIup })}
              sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75, flexShrink: 0, color: '#FF6B00' }}>
              {creatingFor === user.id ? '...' : 'Skapa'}
            </Button>
          </>
        )}
        {iups.length > 1 && (
          <IconButton size="small"
            onClick={() => setExpandedHist(p => ({ ...p, [user.id]: !histOpen }))}>
            <HistoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        )}
        {hasChildren && (
          <IconButton size="small"
            onClick={() => setExpandedChildren(p => ({ ...p, [user.id]: !childOpen }))}>
            {childOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        )}
      </Box>

      <Collapse in={histOpen}>
        <Box sx={{ pl: depth === 1 ? 6 : 8, pr: 2, pb: 1, bgcolor: bgDepth }}>
          {iups.slice(1).map((iup, idx) => (
            <Box key={iup.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontSize: '0.65rem' }}>
                {iup.period || 'Utan datum'} — {IUP_TYPE_LABELS[iup.type] || 'IUP'}
              </Typography>
              <StatusChip status={iup.status} />
              <Button size="small" variant="text"
                onClick={() => navigate('/hr/iup/' + iup.id)}
                sx={{ fontSize: '0.6rem', minWidth: 0, px: 0.5 }}>
                Öppna
              </Button>
            </Box>
          ))}
        </Box>
      </Collapse>

      {hasChildren && (
        <Collapse in={childOpen}>
          {children.map(child => (
            <MemberRow key={child.user.id} member={child} depth={depth + 1}
              navigate={navigate} onCreateNew={onCreateNew} creatingFor={creatingFor}
              expandedChildren={expandedChildren} setExpandedChildren={setExpandedChildren}
              expandedHist={expandedHist} setExpandedHist={setExpandedHist} />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ── Förväntningschecklista per medarbetare (GL-vy) ─────────────────
function ChecklistPanel({ employeeId, expectations, checks, onToggle }) {
  const checked = checks || {};
  const hasCompany = (expectations || []).some(e => e._origin === 'company');

  if (!expectations || expectations.length === 0) {
    return (
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Inga aktiva förväntningar att bocka av.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pt: 1, pb: 1.5 }}>
      {expectations.map(exp => (
        <Box key={exp.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
          <Checkbox
            size="small"
            checked={!!checked[exp.id]}
            onChange={e => onToggle(employeeId, exp.id, e.target.checked)}
            sx={{ p: 0.25, mt: '-2px', color: '#bdbdbd', '&.Mui-checked': { color: '#FF6B00' } }}
          />
          <Typography variant="caption" sx={{ pt: 0.5, lineHeight: 1.4 }}>
            {exp._origin === 'company' && (
              <Box component="span" sx={{
                display: 'inline-block', width: 7, height: 7,
                borderRadius: '50%', bgcolor: '#FF6B00', mr: 0.5, mb: '-1px',
              }} />
            )}
            {exp.text}
          </Typography>
        </Box>
      ))}
      {hasCompany && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#FF6B00', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            = Bolagsövergripande förväntning
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Toppnivå-kort ─────────────────────────────────────────────────
function TopCard({ member, navigate, onCreateNew, creatingFor,
  expandedChildren, setExpandedChildren, expandedHist, setExpandedHist,
  direction, upstreamDirs, authorId, authorRole,
  viewerIsGL, glExpectations, empChecks, onCheckToggle }) {
  const { user, iups, latestIup, children } = member;
  const childOpen  = !!expandedChildren[user.id];
  const histOpen   = !!expandedHist[user.id];
  const [dirOpen, setDirOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [dirHasContent, setDirHasContent] = useState(!!(direction?.content));

  useEffect(() => { setDirHasContent(!!(direction?.content)); }, [direction]);

  const IUP_TYPE_LABELS = { gruppledare: 'GL-IUP', ao_ansvarig: 'AO-IUP', medarbetare: 'MA-IUP' };
  const initials = (user.displayName || user.email || '?')[0].toUpperCase();
  const hasChildren = children && children.length > 0;
  const childrenWithIUP = children?.filter(c => c.latestIup).length ?? 0;

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: '#FF6B00', fontSize: 15, flexShrink: 0 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight="bold" variant="body2" noWrap>
              {user.displayName || user.email}
            </Typography>
            <Chip size="small" label={user.role || 'medarbetare'}
              sx={{ height: 16, fontSize: '0.6rem', mt: 0.25 }} />
          </Box>
        </Box>

        {latestIup ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Senaste: {latestIup.period || 'Utan datum'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <StatusChip status={latestIup.status} />
              <ProgressBar iup={latestIup} />
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Inget IUP ännu
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            disabled={creatingFor === user.id}
            onClick={() => onCreateNew(member)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' }, fontSize: '0.7rem' }}>
            {creatingFor === user.id ? 'Skapar...' : 'Skapa nytt'}
          </Button>
          {latestIup && (
            <Button size="small" variant="outlined"
              onClick={() => navigate('/hr/iup/' + latestIup.id)}
              sx={{ fontSize: '0.7rem' }}>
              Öppna senaste
            </Button>
          )}
        </Box>
      </Box>

      {/* Riktning */}
      <Divider />
      <Box onClick={() => setDirOpen(v => !v)}
        sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center',
          gap: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
        <TrendingUpIcon sx={{ fontSize: 15, color: dirHasContent ? '#FF6B00' : 'text.secondary' }} />
        <Typography variant="caption" sx={{ flex: 1, color: dirHasContent ? '#FF6B00' : 'text.secondary' }}>
          {dirHasContent ? 'Riktning angiven' : 'Ange riktning'}
        </Typography>
        {dirOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={dirOpen}>
        <DirectionPanel
          targetUserId={user.id}
          authorId={authorId}
          authorRole={authorRole}
          direction={direction}
          upstreamDirs={upstreamDirs}
          onSave={() => setDirHasContent(true)}
        />
      </Collapse>

      {/* Historik */}
      {iups.length > 0 && (
        <>
          <Divider />
          <Box onClick={() => setExpandedHist(p => ({ ...p, [user.id]: !histOpen }))}
            sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center',
              gap: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <HistoryIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {iups.length} samtal totalt
            </Typography>
            {histOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={histOpen}>
            <Box sx={{ px: 2.5, pb: 1.5 }}>
              {iups.map((iup, idx) => (
                <Box key={iup.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
                  borderTop: idx > 0 ? '1px solid' : 'none', borderColor: 'divider',
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={idx === 0 ? 'bold' : 'normal'}>
                      {iup.period || 'Utan datum'}
                      {idx === 0 && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#FF6B00' }}>
                          (senaste)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {IUP_TYPE_LABELS[iup.type] || 'IUP'}
                    </Typography>
                  </Box>
                  <ProgressBar iup={iup} />
                  <StatusChip status={iup.status} />
                  <Button size="small" variant="text"
                    onClick={() => navigate('/hr/iup/' + iup.id)}
                    sx={{ fontSize: '0.65rem', minWidth: 0, px: 1 }}>
                    Öppna
                  </Button>
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}

      {/* Checklista (GL-vy) */}
      {viewerIsGL && (
        <>
          <Divider />
          <Box onClick={() => setCheckOpen(v => !v)}
            sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center',
              gap: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <AssignmentTurnedInIcon sx={{ fontSize: 15,
              color: Object.values(empChecks || {}).some(Boolean) ? '#FF6B00' : 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              Checklista · {Object.values(empChecks || {}).filter(Boolean).length}/{glExpectations?.length || 0} uppfyllda
            </Typography>
            {checkOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={checkOpen}>
            <ChecklistPanel
              employeeId={user.id}
              expectations={glExpectations}
              checks={empChecks}
              onToggle={onCheckToggle}
            />
          </Collapse>
        </>
      )}

      {/* Team-expansion */}
      {hasChildren && (
        <>
          <Divider />
          <Box onClick={() => setExpandedChildren(p => ({ ...p, [user.id]: !childOpen }))}
            sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center',
              gap: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <PeopleIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {children.length} {user.role === 'ao_ansvarig' ? 'gruppledare' : 'medarbetare'}
              {childrenWithIUP > 0 && ` · ${childrenWithIUP} med IUP`}
            </Typography>
            {childOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={childOpen}>
            {children.map(child => (
              <MemberRow key={child.user.id} member={child} depth={1}
                navigate={navigate} onCreateNew={onCreateNew} creatingFor={creatingFor}
                expandedChildren={expandedChildren} setExpandedChildren={setExpandedChildren}
                expandedHist={expandedHist} setExpandedHist={setExpandedHist} />
            ))}
          </Collapse>
        </>
      )}
    </Paper>
  );
}

// ── Mina mål & förväntningar (VD / AO-panel i teamvyn) ────────────
function MyGoalsCard({ myProfile, myExpectations, onNavigate }) {
  const [open, setOpen] = useState(true);
  const isVD = myProfile?.role === 'vd' || (myProfile?.role === 'superadmin' && !myProfile?.departmentId);
  const [showAllMatrix, setShowAllMatrix] = useState(false);
  const hasRole = !!myExpectations?.personalRole?.trim();
  const goals = myExpectations?.personalGoals || [];
  const activeMatrix = (myExpectations?.matrix || []).filter(m => m.active);
  const isEmpty = !hasRole && goals.length === 0 && activeMatrix.length === 0;

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden', borderLeft: '4px solid #FF6B00' }}>
      <Box onClick={() => setOpen(o => !o)}
        sx={{ px: 2.5, py: 1.5, cursor: 'pointer', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          '&:hover': { bgcolor: 'action.hover' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUpIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">Mina mål & förväntningar</Typography>
            <Typography variant="caption" color="text.secondary">
              {isVD ? 'Bolagsövergripande' : 'Affärsområdet'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined"
            onClick={e => { e.stopPropagation(); onNavigate('/hr/forvantan'); }}
            sx={{ fontSize: '0.75rem', borderColor: '#FF6B00', color: '#FF6B00',
              '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}>
            Redigera
          </Button>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 2.5, py: 2 }}>
          {isEmpty ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Du har inte satt dina mål och förväntningar ännu.
              </Typography>
              <Button size="small" variant="contained"
                onClick={() => onNavigate('/hr/forvantan')}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' }, flexShrink: 0 }}>
                Sätt mål
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {hasRole && (
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
                    MIN ROLL
                  </Typography>
                  <Typography variant="body2">{myExpectations.personalRole}</Typography>
                </Grid>
              )}
              {goals.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
                    MINA MÅL
                  </Typography>
                  {goals.map((g, i) => (
                    <Typography key={i} variant="body2">· {g}</Typography>
                  ))}
                </Grid>
              )}
              {activeMatrix.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
                    FÖRVÄNTNINGSMATRIS
                  </Typography>
                  {(showAllMatrix ? activeMatrix : activeMatrix.slice(0, 5)).map(m => (
                    <Typography key={m.id} variant="body2">· {m.text}</Typography>
                  ))}
                  {activeMatrix.length > 5 && (
                    <Button size="small" variant="text"
                      onClick={e => { e.stopPropagation(); setShowAllMatrix(v => !v); }}
                      sx={{ mt: 0.25, p: 0, fontSize: '0.75rem', color: '#FF6B00',
                        textTransform: 'none', minWidth: 0 }}>
                      {showAllMatrix ? 'Visa färre' : `+${activeMatrix.length - 5} till`}
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Huvud-komponent ───────────────────────────────────────────────
export default function IUPTeam() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [teamMembers, setTeamMembers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const creatingFor = null;
  const [expandedChildren, setExpandedChildren] = useState({});
  const [expandedHist, setExpandedHist] = useState({});

  const [myProfile, setMyProfile] = useState(null);
  const [myDeptName, setMyDeptName] = useState('');
  const [myDirections, setMyDirections] = useState({});
  const [myGeneralDir, setMyGeneralDir] = useState(null);
  const [vdGeneralDir, setVdGeneralDir] = useState(null);
  const [upstreamDirs, setUpstreamDirs] = useState([]);
  const [myExpectations, setMyExpectations] = useState(null);
  const [glExpectations, setGlExpectations] = useState([]);
  const [checklists, setChecklists] = useState({});
  const [createDialog, setCreateDialog] = useState({ open: false, member: null });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const meSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const me = meSnap.exists() ? { id: currentUser.uid, ...meSnap.data() } : { id: currentUser.uid };
      setMyProfile(me);

      const isGL = me.role === 'gruppledare';
      const isAO = me.role === 'ao_ansvarig';
      const isVD = me.role === 'vd';
      const isSuperadmin = me.role === 'superadmin';

      const hasDept = !!me.departmentId;
      const effectiveRole = isGL ? 'gruppledare'
        : isAO ? 'ao_ansvarig'
        : isVD ? 'vd'
        : isSuperadmin && hasDept ? 'ao_ansvarig'
        : isSuperadmin ? 'superadmin'
        : null;

      if (!effectiveRole) { setLoading(false); return; }

      const allDeptsSnap = await getDocs(collection(db, 'departments'));
      const allDepts = allDeptsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const myDept = allDepts.find(d => d.id === me.departmentId);
      setMyDeptName(myDept?.name || '');

      const depth = (effectiveRole === 'vd' || effectiveRole === 'superadmin') ? 2
        : effectiveRole === 'ao_ansvarig' ? 1 : 0;

      const [tree] = await Promise.all([
        loadTeamTree(me, allDepts, depth, currentUser.uid, effectiveRole),
      ]);
      setTeamMembers(tree);

      // Load my own expectations (VD/AO panel)
      try {
        const expDocId = (isVD || isSuperadmin) ? `company_${currentUser.uid}` : `area_${currentUser.uid}`;
        const expSnap = await getDoc(doc(db, 'iupExpectations', expDocId));
        if (expSnap.exists()) setMyExpectations(expSnap.data());
      } catch (_) {}

      // GL: load expectations matrix + existing checklists for employees
      if (isGL) {
        try {
          // Bara företagsnivån och den egna avdelningens förväntningar – det
          // är vad gruppledare får läsa (firestore.rules, iupExpectations)
          const [companySnap, areaSnap] = await Promise.all([
            getDocs(query(collection(db, 'iupExpectations'), where('scope', '==', 'company'))),
            me.departmentId
              ? getDocs(query(collection(db, 'iupExpectations'),
                  where('scope', '==', 'area'), where('departmentId', '==', me.departmentId)))
              : Promise.resolve({ docs: [] }),
          ]);
          const companyDoc = companySnap.docs[0]?.data();
          const areaDoc = areaSnap.docs[0]?.data();
          const combined = [
            ...(companyDoc?.matrix?.filter(m => m.active).map(m => ({
              ...m, id: `c_${m.id}`, _origin: 'company',
            })) || []),
            ...(areaDoc?.matrix?.filter(m => m.active).map(m => ({
              ...m, id: `a_${m.id}`, _origin: 'area',
            })) || []),
          ];
          setGlExpectations(combined);
        } catch (_) {}

        try {
          const clSnap = await getDocs(query(
            collection(db, 'iupChecklists'),
            where('glId', '==', currentUser.uid)
          ));
          const cls = {};
          clSnap.docs.forEach(d => {
            const data = d.data();
            cls[data.employeeId] = data.checks || {};
          });
          setChecklists(cls);
        } catch (_) {}
      }

      // Load my authored directions
      try {
        const myDirSnap = await getDocs(query(
          collection(db, 'iupDirections'),
          where('authorId', '==', currentUser.uid)
        ));
        const byTarget = {};
        let genDir = null;
        myDirSnap.docs.forEach(d => {
          const data = { id: d.id, ...d.data() };
          if (data.scope === 'general') genDir = data;
          else if (data.scope === 'individual' && data.targetUserId) byTarget[data.targetUserId] = data;
        });
        setMyGeneralDir(genDir);
        setMyDirections(byTarget);
      } catch (_) {}

      // Load all general directions for upstream context
      try {
        const genSnap = await getDocs(query(
          collection(db, 'iupDirections'),
          where('scope', '==', 'general')
        ));
        const allGenDirs = genSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const vdD = allGenDirs.find(d => d.authorRole === 'vd' || d.authorRole === 'superadmin');
        setVdGeneralDir(vdD || null);

        const upstream = [];
        if (vdD && effectiveRole !== 'vd' && effectiveRole !== 'superadmin') {
          upstream.push({ label: 'VDs generella riktning för bolaget', content: vdD.content });
        }
        if (effectiveRole === 'gruppledare') {
          const aoD = allGenDirs.find(d =>
            d.authorRole === 'ao_ansvarig' && d.departmentId === me.departmentId
          );
          if (aoD) upstream.push({ label: 'AOs generella riktning för affärsområdet', content: aoD.content });
        }
        setUpstreamDirs(upstream);
      } catch (_) {}

      // Olästa notiser
      try {
        const notifSnap = await getDocs(query(
          collection(db, 'notifications'),
          where('toUserId', '==', currentUser.uid),
          where('read', '==', false)
        ));
        setUnreadCount(notifSnap.docs.length);
      } catch (_) {}

    } catch (e) {
      console.error('IUPTeam loadData:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleCreateNew(member) {
    setCreateDialog({ open: true, member });
    setCreateError('');
  }

  async function handleCreateMode(mode) {
    const { member } = createDialog;
    setCreateLoading(true);
    setCreateError('');
    try {
      let formData = {};
      if (mode === 'copy' && member.latestIup) {
        formData = null; // createIupForUser handles copy internally
      } else if (mode === 'ai' && member.latestIup) {
        formData = await summarizeIupAI(member.latestIup);
      }
      // For 'empty', formData stays {}
      const iupToUse = mode === 'copy' ? member.latestIup : null;
      const newId = await createIupForUser(member.user, currentUser.uid, iupToUse, formData);
      setCreateDialog({ open: false, member: null });
      navigate('/hr/iup/' + newId);
    } catch (e) {
      setCreateError(e.message || 'Något gick fel.');
    }
    setCreateLoading(false);
  }

  async function handleCheckToggle(employeeId, itemId, checked) {
    const docId = `${currentUser.uid}_${employeeId}`;
    const current = checklists[employeeId] || {};
    const updated = { ...current, [itemId]: checked };
    setChecklists(prev => ({ ...prev, [employeeId]: updated }));
    try {
      await setDoc(doc(db, 'iupChecklists', docId), {
        glId: currentUser.uid, employeeId,
        checks: updated, updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error('Checklist save error:', e);
    }
  }

  const submitted = teamMembers.filter(m => m.latestIup?.status === 'submitted').length;
  const reviewed  = teamMembers.filter(m => m.latestIup?.status === 'reviewed').length;
  const withIUP   = teamMembers.filter(m => m.latestIup).length;

  const showGeneralCard = myProfile && (
    myProfile.role === 'vd' ||
    myProfile.role === 'superadmin' ||
    myProfile.role === 'ao_ansvarig'
  );

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small"><ArrowBackIcon /></IconButton>
        <Typography variant="subtitle1" fontWeight="bold">Mitt team – IUP-samtal</Typography>
      </Box>
      <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Mitt team – IUP-samtal</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Teamöversikt och individuella utvecklingsplaner
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Chip label={`${teamMembers.length} direktrapporter`} variant="outlined" />
          <Chip label={`${withIUP} med IUP`}
            color={withIUP > 0 ? 'primary' : 'default'}
            variant={withIUP > 0 ? 'filled' : 'outlined'} />
          <Chip label={`${submitted} inlämnade`}
            sx={submitted > 0 ? { bgcolor: '#FF6B00', color: '#fff' } : {}}
            variant={submitted > 0 ? 'filled' : 'outlined'} />
          <Chip label={`${reviewed} genomförda`}
            color={reviewed > 0 ? 'success' : 'default'}
            variant={reviewed > 0 ? 'filled' : 'outlined'} />
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} olästa`} sx={{ bgcolor: '#d32f2f', color: '#fff' }} />
          )}
        </Box>

        {(myProfile?.role === 'vd' || myProfile?.role === 'superadmin' ||
          myProfile?.role === 'ao_ansvarig' || myProfile?.isDepartmentManager) && (
          <MyGoalsCard
            myProfile={myProfile}
            myExpectations={myExpectations}
            onNavigate={navigate}
          />
        )}

        {showGeneralCard && (
          <GeneralDirectionCard
            myRole={myProfile.role}
            myId={currentUser.uid}
            departmentId={myProfile.departmentId}
            departmentName={myDeptName}
            vdDir={vdGeneralDir}
            myDir={myGeneralDir}
            onSave={updated => setMyGeneralDir(prev => ({ ...prev, ...updated }))}
          />
        )}

        {teamMembers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Inga teammedlemmar hittades. Kontrollera att medarbetare är kopplade
              till rätt roll och avdelning i Admin.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {teamMembers.map(member => (
              <Grid item xs={12} sm={6} md={4} key={member.user.id}>
                <TopCard
                  member={member}
                  navigate={navigate}
                  onCreateNew={handleCreateNew}
                  creatingFor={creatingFor}
                  expandedChildren={expandedChildren}
                  setExpandedChildren={setExpandedChildren}
                  expandedHist={expandedHist}
                  setExpandedHist={setExpandedHist}
                  direction={myDirections[member.user.id] || null}
                  upstreamDirs={upstreamDirs}
                  authorId={currentUser.uid}
                  authorRole={myProfile?.role}
                  viewerIsGL={myProfile?.role === 'gruppledare'}
                  glExpectations={glExpectations}
                  empChecks={checklists[member.user.id]}
                  onCheckToggle={handleCheckToggle}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Dialog: välj hur nytt IUP ska skapas */}
      <Dialog open={createDialog.open} onClose={() => !createLoading && setCreateDialog({ open: false, member: null })}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Skapa nytt IUP — {createDialog.member?.user?.displayName || ''}
        </DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Välj hur det nya IUP-arket ska fyllas i:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="outlined" fullWidth disabled={createLoading}
              onClick={() => handleCreateMode('empty')}
              sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left' }}>
              <Box>
                <Typography fontWeight="bold" variant="body2">Tomt ark</Typography>
                <Typography variant="caption" color="text.secondary">
                  Börja från början utan förval
                </Typography>
              </Box>
            </Button>
            {createDialog.member?.latestIup && (
              <>
                <Button variant="outlined" fullWidth disabled={createLoading}
                  onClick={() => handleCreateMode('copy')}
                  sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left' }}>
                  <Box>
                    <Typography fontWeight="bold" variant="body2">Kopiera från föregående</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fyller i samma svar som senaste IUP
                    </Typography>
                  </Box>
                </Button>
                <Button variant="outlined" fullWidth disabled={createLoading}
                  onClick={() => handleCreateMode('ai')}
                  sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left',
                    borderColor: '#FF6B00', color: '#FF6B00',
                    '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}>
                  <Box>
                    {createLoading
                      ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: '#FF6B00' }} />
                          <Typography variant="body2">AI sammanfattar…</Typography>
                        </Box>
                      : <>
                          <Typography fontWeight="bold" variant="body2">AI-sammanfattning</Typography>
                          <Typography variant="caption" color="text.secondary">
                            AI kondenserar föregående IUP till kortare startpunkter
                          </Typography>
                        </>
                    }
                  </Box>
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog({ open: false, member: null })} disabled={createLoading}>
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
